<template>
  <v-card class="mb-3">
    <v-card-text class="container">
      <v-row v-if="!isDataset && (!readonly || contains(editableItem, 'synonym'))">
         <v-dialog
          v-model="synonymModal.state"
          width="500"
          persistent
        >
          <v-card>
            <AdCardTitle dialog>{{ $t('add-synonym') }}</AdCardTitle>
            <v-card-text class="pa-5">
              <v-form ref="synonymForm" v-model="synonymModal.formIsValid" @submit.prevent>
              <v-row>
                <v-col cols="10">
                  <EditableString
                    ref="synonymInput"
                    v-model="synonymModal.value['#text']"
                    :label="$t('synonym')"
                    required
                    :rules="[synonymAlreadyExists]"
                  />
                </v-col>
                <v-col cols="2" class="d-flex align-center">
                  <EditableLanguageButton
                    v-model="synonymModal.value.language"
                    :locales="projectLocales"
                    @input="validateSynonym"
                  />
                </v-col>
              </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions class="dialogbottomstrip">
              <v-spacer></v-spacer>
              <AdButton type="cancel" @click="closeSynonymModal" />
              <AdButton
                type="add"
                :disabled="!synonymModal.formIsValid"
                @click="saveSynonym"
              />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-col>
          <div class="d-flex">
            <span style="flex: 0 0 200px" :class="!readonly ? 'valueDomainLabel' : 'mr-4'">{{ $t('synonyms') }}</span>
            <AdButton v-if="!readonly" type="add" small list @click="startAddSynonym" />
            <div>
              <v-chip
                v-for="(syn, i) in synonymFilter"
                :key="`syn-${i}`"
                class="mr-2 my-1"
                :close="!readonly"
                @click:close="removeSynonym(syn)"
              >
              <flag
                v-if="syn.language"
                class="mr-1"
                :squared="false"
                :iso="syn.language.substring(3, 5)"
              />
              {{ syn['#text'] }}
            </v-chip>
            <v-chip
              v-if="synonymsInOtherLanguages > 0"
              class="my-1"
            >+{{ synonymsInOtherLanguages }}</v-chip>
            </div>

          </div>
        </v-col>
      </v-row>
      <v-row v-if="!isDataset && (!readonly || contains(editableItem, 'operationalization'))">
        <v-col cols="12" class="py-2">
          <div v-if="!contains(editableItem, 'operationalization')">
            <span class="valueDomainLabel">{{ $t('operationalization') }}</span>
            <AdButton v-if="!readonly" type="add" small list @click="addMetaItem('operationalization')" />
          </div>
          <div v-else>
            <EditableTranslationEditor
              v-model="editableItem.operationalization"
              ref="operationalization"
              :locales="locales"
              :readonly="readonly"
              :label="$t('operationalization')"
              editor
              dense
              @change="saveEditedValue('operationalization', $event)"
              :minHeight="editorMinHeight"
            />
          </div>
        </v-col>
      </v-row>

      <v-row v-if="!isDataset && (!readonly || contains(editableItem, 'source'))">
        <v-col cols="12" class="py-2">
          <div v-if="!contains(editableItem, 'source')">
            <span class="valueDomainLabel">{{ $t('source') }}</span>
            <AdButton v-if="!readonly" type="add" small list @click="addMetaItem('source')" />
          </div>
          <div v-else>
            <EditableTranslationEditor
              v-model="editableItem.source"
              ref="source"
              :locales="locales"
              :readonly="readonly"
              :label="$t('source')"
              editor
              dense
              @change="saveEditedValue('source', $event)"
              :minHeight="editorMinHeight"
            />
          </div>
        </v-col>
      </v-row>

      <v-row v-if="!isDataset && (!readonly || contains(editableItem, 'rationale'))">
        <v-col cols="12" class="py-2">
          <div v-if="!contains(editableItem, 'rationale')">
            <span class="valueDomainLabel">{{ $t('rationale') }}</span>
            <AdButton v-if="!readonly" type="add" small list @click="addMetaItem('rationale')" />
          </div>
          <div v-else>
            <EditableTranslationEditor
              v-model="editableItem.rationale"
              ref="rationale"
              :locales="locales"
              :readonly="readonly"
              :label="$t('rationale')"
              editor
              dense
              @change="saveEditedValue('rationale', $event)"
              :minHeight="editorMinHeight"
            />
          </div>
        </v-col>
      </v-row>

      <v-row v-if="!isDataset && (!readonly || contains(editableItem, 'comment') || contains(editableItem, 'inheritedComment'))">
        <AddCommentModal v-model="commentModal.state" :selected="commentModal.selected" :onCancel="closeCommentModal" @save="commentModal.onSave"/>
        <v-col cols="12" class="py-2">
          <div v-if="!(contains(editableItem, 'comment') || contains(editableItem, 'inheritedComment'))">
            <span class="valueDomainLabel">{{ $t('comment') }}</span>
            <AdButton v-if="!readonly" type="add" small list @click="addMetaItem('comment')" />
          </div>
          <div v-else>
            <v-data-table
             dense
              :caption="$t('comment')"
              :hide-default-footer="comments.length <= 10"
              :items="filteredComments"
              sort-by="language"
              :headers="filteredCommentHeaders"
            >
              <template #header.text>{{ $t('text') }}</template>
              <template #header.language>{{ $t('language') }}</template>
              <template #header.actions>
                 <AdButton v-if="!readonly" noMarginRight small type="add" @click="startAddComment"/>
              </template>
              <template #item.text="{item}">
                <div class="d-flex align-center" style="gap: 8px">
                  <v-tooltip v-if="item.inherited" content-class="d-block" bottom>
                    <template #activator="{ on }">
                      <v-icon small v-on="on">mdi-link-variant</v-icon>
                    </template>
                    <span>{{ $t('inherited-comment')}}</span>
                  </v-tooltip>
                  <div v-html="item['#text']" class="no-margins-inside"></div>
                </div>
              </template>
              <template #item.language="{item}">
                <flag
                  v-if="item.language"
                  class="ml-1"
                  :squared="false"
                  :iso="item.language.substring(3, 5)"
                />
              </template>
              <template #item.actions="{ item }">
                <AdSpeedDial v-if="!readonly && !item.inherited" direction="left">
                  <AdSpeedOption type="edit" @click="startEditComment(item)"/>
                  <AdSpeedOption type="delete" @click="deleteComment(item)" />
                </AdSpeedDial>
              </template>
            </v-data-table>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="!readonly || properties.length">
        <!-- Modal for adding / editing properties -->
        <v-dialog
          v-model="propertyModal.state"
          width="800"
          persistent
        >
          <v-card>
            <AdCardTitle dialog>{{ propertyModalTitle }}</AdCardTitle>
            <v-card-text class="pa-5">
              <v-form v-model="propertyModal.formIsValid">
               <v-row>
                <v-col cols="6">
                  <EditableString
                    v-model="propertyModal.value.name"
                    :label="$t('property')"
                    required
                    :rules="[propNameIsUnique, inputIsNotEmpty]"
                    :disabled="this.propertyModal.mode === 'edit'"
                  />
                </v-col>
                <v-col cols="12">
                  <TextEditor
                    v-model="propertyModal.value['#text']"
                    :label="$t('value')"
                    required
                    emitOnInput
                    :minHeight="editorMinHeight"
                  />
                </v-col>
               </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions class="dialogbottomstrip">
              <v-spacer></v-spacer>
              <AdButton type="cancel" @click="closePropertyModal" />
              <AdButton
                :type="propertyModal.mode === 'add' ?'add' : 'save'"
                :disabled="!propertyModal.formIsValid || descriptionIsEmpty"
                @click="saveProperty"
              />
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!-- Property display section -->
        <v-col cols="12" class="py-2">
          <div v-if="!properties.length">
            <span class="valueDomainLabel">{{ $t('property') }}</span>
            <AdButton v-if="!readonly" type="add" small list @click="startAddProperty" />
          </div>
          <div v-else>
            <v-data-table
              dense
              :caption="$t('properties')"
              :hide-default-footer="properties.length <= 5"
              :items="properties"
              :headers="propertyHeaders"
            >
              <template #header.name>
                {{ $t('property')}}
              </template>
              <template #header.#text>
                {{ $t('value')}}
              </template>
               <template #header.actions>
                 <AdButton v-if="!readonly" noMarginRight small type="add" @click="startAddProperty"/>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <AdSpeedDial v-if="!readonly" direction="left">
                  <AdSpeedOption type="edit" @click="startEditProperty(item)"/>
                  <AdSpeedOption type="delete" @click="deleteProperty(item)" />
                </AdSpeedDial>
              </template>
              <template v-slot:[`item.#text`]="{ item }">
                <TextEditorRenderer class="pt-3">
                  <truncate
                    v-if="item['#text']"
                    :text="item['#text']"
                    :length="100"
                    :clamp="$t('seemore')"
                    :less="$t('showless')"
                    type="html"
                    collapsedTextClass="text--disabled"
                  />
                </TextEditorRenderer>
              </template>
            </v-data-table>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="showNoMetaDataHint">
        <v-col cols="12" class="py-2">
          {{ $t('no-items') }}
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { cloneDeep } from 'lodash'
import truncate from 'vue-truncate-collapsed'
import AddCommentModal from './AddCommentModal.vue'

const initialPropertyModalState = {
  state: false,
  mode: 'add',
  value: { name: '', '#text': '' },
  formIsValid: false
}
const initialSynonymModalState = {
  state: false,
  value: { language: '', '#text': '' },
  formIsValid: false
}
const initialCommentModalState = {
  state: false,
  selected: null,
  onSave: () => {}
}
export default {
  name: 'DatasetMetaInformation',
  components: {
    truncate,
    AddCommentModal
  },
  props: {
    value: {
      type: Object,
      required: false,
      default: () => {}
    },
    // If set to true, only parts relevant to datasets are offered. Otherwise (default) this component is in 'dataset concept' mode.
    isDataset: {
      type: Boolean,
      default: false
    },
    locales: {
      type: Array,
      required: false,
      default: () => []
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      test: '',
      editableItem: {},
      metaItems: {},
      commentHeaders: [
        { value: 'text', sortable: false, width: 'auto' },
        { value: 'language', sortable: false },
        { value: 'actions', sortable: false, width: '150px', align: 'right', condition: () => !this.readonly }
      ],
      propertyHeaders: [
        { value: 'name', width: '20%' },
        { value: '#text', sortable: false },
        { value: 'actions', sortable: false, align: 'right' }
      ],
      propertyModal: cloneDeep(initialPropertyModalState),
      synonymModal: cloneDeep(initialSynonymModalState),
      commentModal: cloneDeep(initialCommentModalState)
    }
  },
  computed: {
    synonymFilter () {
      if (this.readonly) {
        return this.$filters.getMatchingDesignations({
          value: this.editableItem.synonym || []
        })
      }
      return this.editableItem.synonym || []
    },
    synonymsInOtherLanguages () {
      return this.editableItem.synonym?.length - this.synonymFilter.length
    },
    filteredCommentHeaders () {
      return this.commentHeaders.filter(h => typeof h.condition !== 'function' || h.condition())
    },
    descriptionIsEmpty () {
      return this.propertyModal.value['#text'] === null ||
        !this.$filters.htmlHasTextContent(this.propertyModal.value['#text'])
    },
    editorMinHeight () {
      return this.readonly ? 0 : 160
    },
    comments () {
      return this.editableItem.comment || []
    },
    inheritedComments () {
      return this.editableItem.inheritedComment?.map(comment => ({ ...comment, inherited: true })) || []
    },
    filteredComments () {
      const comments = [...this.comments, ...this.inheritedComments]
      if (!this.readonly) return comments
      return comments.filter(comment => comment.language === this.$store.getters['app/projectLanguage'])
    },
    properties () {
      // Return all properties exept 'Keywords'
      return this.editableItem.property?.filter(prop => prop.name !== 'Keyword') || []
    },
    propertyModalTitle () {
      return this.$t(`${this.propertyModal.mode}-property`)
    },
    /**
     * Show no meta data hint.
     */
    showNoMetaDataHint () {
      return !this.isDataset &&
        this.readonly === true &&
        this.contains(this.editableItem, 'synonym') === false &&
        this.contains(this.editableItem, 'operationalization') === false &&
        this.contains(this.editableItem, 'source') === false &&
        this.contains(this.editableItem, 'rationale') === false &&
        this.contains(this.editableItem, 'comment') === false &&
        !this.properties.length
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler (value) {
        // Clone item.
        this.editableItem = this.$filters.clone(value) || {}
      }
    }
  },
  methods: {
    /**
     * Add meta item.
     *
     * @param type
     */
    addMetaItem (type) {
      this.$set(this.editableItem, type, [])
      this.metaItems[type] = true
      this.$nextTick(() => {
        if (typeof this.$refs[type]?.focus === 'function') {
          this.$refs[type].focus()
        }
      })
    },
    closePropertyModal () {
      this.propertyModal = cloneDeep(initialPropertyModalState)
    },
    closeCommentModal () {
      this.commentModal = cloneDeep(initialCommentModalState)
    },
    closeSynonymModal () {
      this.synonymModal = cloneDeep(initialSynonymModalState)
      if (this.$refs.synonymForm) {
        this.$refs.synonymForm.resetValidation()
      }
    },
    /**
     * Check if item contains type.
     *
     * @param item
     * @param type
     */
    contains (item, type) {
      // Check if item type is array.
      return Array.isArray(item?.[type]) === true &&
        (
          item?.[type].filter((i) => String(i?.['#text'] || '').length > 0).length > 0 ||
          this.metaItems?.[type] === true
        )
    },
    deleteProperty (item) {
      const indexToDelete = this.editableItem?.property?.findIndex(prop => prop.name === item.name && prop['#text'] === item['#text'])
      if (indexToDelete > -1) {
        this.editableItem.property.splice(indexToDelete, 1)
        this.$emit('input', this.editableItem)
      }
    },
    inputIsNotEmpty (input) {
      return input?.trim() !== ''
    },
    propNameIsUnique (input) {
      if (this.propertyModal.mode === 'edit') {
        return true
      }
      const existingPropNames = this.editableItem?.property?.map(prop => prop.name) || []
      return !existingPropNames?.includes(input) || this.$t('property-already-exists')
    },
    synonymAlreadyExists (input) {
      const existingSynonym = this.editableItem?.synonym?.find(syn => syn['#text'] === input && syn.language === this.synonymModal.value.language)
      return !existingSynonym || this.$t('synonym-already-exists')
    },
    validateSynonym () {
      if (this.$refs.synonymForm && this.$refs.synonymInput) {
        this.synonymModal.value['#text'] = this.$refs.synonymInput.editableValue
        this.$refs.synonymForm.validate()
      }
    },
    removeSynonym (syn) {
      const indexToRemove = this.editableItem?.synonym?.indexOf(syn)
      if (indexToRemove > -1) {
        this.editableItem.synonym.splice(indexToRemove, 1)
        this.$emit('input', this.editableItem)
      }
    },
    /**
     * Save edit changes.
     *
     * @param type
     * @param value
     */
    saveEditedValue (type, value) {
      this.editableItem[type] = value
      this.$emit('input', this.editableItem)
      this.metaItems[type] = false
    },
    saveProperty () {
      const propToUpdate = this.editableItem.property.find(prop => prop.name === this.propertyModal.value.name)
      if (propToUpdate) {
        propToUpdate['#text'] = this.propertyModal.value['#text']
      } else {
        this.editableItem.property.push(this.propertyModal.value)
      }
      this.$emit('input', this.editableItem)
      this.closePropertyModal()
    },
    saveSynonym () {
      if (!Array.isArray(this.editableItem?.synonym)) {
        return
      }
      this.editableItem.synonym.push(this.synonymModal.value)
      this.$emit('input', this.editableItem)
      this.closeSynonymModal()
    },
    startAddComment () {
      if (!Array.isArray(this.editableItem.comment)) {
        this.$set(this.editableItem, 'comment', [])
      }
      this.commentModal.onSave = (newComment) => {
        this.editableItem.comment.push(newComment)
        this.$emit('input', this.editableItem)
        this.closeCommentModal()
      }
      this.commentModal.state = true
    },
    startEditComment (comment) {
      const oldIndex = this.editableItem.comment.indexOf(comment)
      this.commentModal.selected = comment
      this.commentModal.onSave = (newComment) => {
        this.$set(this.editableItem.comment, oldIndex, newComment)
        this.$emit('input', this.editableItem)
        this.closeCommentModal()
      }
      this.commentModal.state = true
    },
    deleteComment (item) {
      const index = this.editableItem.comment.indexOf(item)
      if (index > -1) {
        this.editableItem.comment.splice(index, 1)
        this.$emit('input', this.editableItem)
      }
    },
    startAddProperty () {
      if (!this.editableItem.property) {
        this.$set(this.editableItem, 'property', [])
      }
      this.propertyModal.mode = 'add'
      this.propertyModal.state = true
    },
    startEditProperty (property) {
      this.propertyModal.mode = 'edit'
      this.propertyModal.value = property
      this.propertyModal.state = true
    },
    async startAddSynonym () {
      if (!this.editableItem.synonym) {
        this.$set(this.editableItem, 'synonym', [])
      }
      this.synonymModal.value.language = this.$store.getters['app/projectLanguage']
      this.synonymModal.state = true
      await this.$nextTick()
      if (this.$refs.synonymInput) {
        this.$refs.synonymInput.focus()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.valueDomainLabel {
  background-color: #ddd;
  padding: 6px 12px 6px 12px;
  float: left;
  width: 200px;
}
::v-deep table caption {
  text-align: left;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: .5rem;
  caption-side: top;
}
.no-margins-inside {
  >  ::v-deep * {
    margin: 0
  }
}
.container:empty {
  display: none;
}
</style>
